<template>
  <div v-if="visible" class="add-task-popup-overlay">
    <div class="add-task-popup">
      <div class="add-task-popup-header">
        <h2>Taak toevoegen</h2>
        <X :stroke-width="1" class="close-icon" @click="$emit('close')" />
      </div>
      <form @submit.prevent="submitTask">
        <div class="form-block">
          <p class="form-block-title">Klant</p>
          <div class="form-grid">
            <div class="form-section client-search-section">
              <label>Klant</label>
              <div class="input-icon-wrapper input-icon-right">
                <input
                  type="text"
                  v-model="searchClient"
                  placeholder="Zoek klant..."
                  @input="onClientInput"
                  @focus="onClientFocus"
                  @keydown.down.prevent="moveClientSelection(1)"
                  @keydown.up.prevent="moveClientSelection(-1)"
                  @keydown.enter.prevent="selectHighlightedClient"
                  @blur="onClientBlur"
                />
                <Search :stroke-width="1" class="input-icon" />
              </div>
              <ul
                v-if="showClientSuggestions"
                class="prospectie-suggestions-dropdown"
              >
                <li
                  v-for="(client, idx) in filteredClientResults"
                  :key="client.id"
                  :class="[
                    'suggestion-item',
                    { active: idx === activeClientIndex },
                  ]"
                  @mousedown.prevent="selectClient(client)"
                >
                  <span class="suggestion-text">{{ client.name }}</span>
                </li>
              </ul>
            </div>
            <div class="form-section">
              <label>Deal</label>
              <CustomDropdown
                v-model="selectedDeal"
                :options="[
                  { value: '', label: 'Selecteer deal' },
                  ...loadedDeals.map((deal) => ({
                    value: deal._id || deal.id,
                    label: deal.title || deal.name,
                  })),
                ]"
                placeholder="Selecteer deal"
              />
            </div>
            <div class="form-section">
              <label>Project</label>
              <CustomDropdown
                v-model="selectedProject"
                :options="[
                  { value: '', label: 'Selecteer project' },
                  ...loadedProjects.map((project) => ({
                    value: project._id || project.id,
                    label: project.name || project.title,
                  })),
                ]"
                placeholder="Selecteer project"
              />
            </div>
          </div>
        </div>
        <div class="form-block">
          <p class="form-block-title">Beschrijving</p>
          <div class="form-grid">
            <div class="form-section">
              <label>Type</label>
              <CustomDropdown
                v-model="taskType"
                :options="[
                  { value: 'administration', label: 'Administratie' },
                  { value: 'development', label: 'Ontwikkeling' },
                  { value: 'night_hours', label: 'Nachturen' },
                  { value: 'onboarding', label: 'Onboarding' },
                ]"
                placeholder="Selecteer type"
              />
            </div>
            <div class="form-section">
              <label>Verantwoordelijke</label>
              <CustomDropdown
                v-model="responsible"
                :options="[
                  {
                    value: '',
                    label: 'Selecteer verantwoordelijke',
                    avatar: null,
                  },
                  ...loadedUsers.map((user) => ({
                    value: user._id || user.id,
                    label: user.name || user.firstName + ' ' + user.lastName,
                    avatar: user.avatar || null,
                  })),
                ]"
                placeholder="Selecteer verantwoordelijke"
                :hideAvatar="false"
              />
            </div>
            <div class="form-section grid-span-2">
              <label>Titel</label>
              <input
                type="text"
                v-model="taskTitle"
                placeholder="Titel taak..."
              />
            </div>
            <div class="form-section grid-span-2">
              <label>Beschrijving</label>
              <textarea
                v-model="taskDescription"
                placeholder="Beschrijving..."
              />
            </div>
            <div class="form-section">
              <label>Opleverdatum</label>
              <div
                style="
                  position: relative;
                  display: flex;
                  align-items: center;
                  gap: 0.5em;
                "
              >
                <span
                  style="
                    position: absolute;
                    left: 1em;
                    pointer-events: none;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    height: 100%;
                  "
                >
                  <Calendar
                    :stroke-width="1"
                    style="
                      width: 1.2em;
                      height: 1.2em;
                      color: #fff;
                      opacity: 0.7;
                    "
                  />
                </span>
                <input
                  type="text"
                  class="date-input"
                  :value="formatDueDate(dueDate)"
                  readonly
                  @click="showDueDateCalendar = true"
                  placeholder="Kies een datum"
                  style="
                    padding-left: 3em;
                    cursor: pointer;
                    background: #222;
                    color: #fff;
                    border-radius: 6px;
                    border: 1px solid var(--border, #e5e7eb);
                    padding: 0.5rem 1rem;
                    width: 100%;
                  "
                />
                <button
                  v-if="dueDate"
                  type="button"
                  @click="clearDueDate"
                  style="
                    background: none;
                    border: none;
                    color: #fff;
                    cursor: pointer;
                  "
                >
                  &times;
                </button>
                <div
                  v-if="showDueDateCalendar"
                  class="calendar-popup"
                  @click.stop
                >
                  <CalendarComponent
                    v-model="dueDate"
                    @update:modelValue="showDueDateCalendar = false"
                    :min-value="null"
                    :max-value="null"
                    layout="month-and-year"
                    class="custom-calendar"
                  />
                </div>
              </div>
            </div>

            <div class="form-section">
              <label>Tijdsduur</label>
              <div class="input-icon-wrapper">
                <Timer :stroke-width="1" class="input-icon" />
                <input type="time" v-model="duration" step="60" />
              </div>
            </div>
            <div class="form-section">
              <label>Prioriteit</label>
              <CustomDropdown
                v-model="priority"
                :options="[
                  { value: '', label: 'Selecteer prioriteit' },
                  { value: 'laag', label: 'Laag' },
                  { value: 'normaal', label: 'Normaal' },
                  { value: 'hoog', label: 'Hoog' },
                ]"
                placeholder="Selecteer prioriteit"
              />
            </div>
            <div class="form-section">
              <label>Herhaling</label>
              <ToggleSwitch v-model="showRepeatOptions" />
            </div>
          </div>
        </div>

        <div v-if="showRepeatOptions" class="repeat-section">
          <p class="form-block-title">Herhaling</p>

          <div class="repeat-field">
            <label>Repeat patroon</label>
            <CustomDropdown
              v-model="repeatData.pattern"
              :options="[
                { value: '', label: 'Selecteer patroon' },
                { value: 'daily', label: 'Dagelijks' },
                { value: 'workdays', label: 'Elke werkdag (Ma-Vr)' },
                { value: 'weekly', label: 'Wekelijks' },
                { value: 'monthly', label: 'Maandelijks' },
                { value: 'yearly', label: 'Jaarlijks' },
              ]"
              placeholder="Selecteer patroon"
            />
          </div>

          <div
            v-if="['weekly'].includes(repeatData.pattern)"
            class="repeat-field"
          >
            <label>Repeat every</label>
            <CustomDropdown
              v-model.number="repeatData.interval"
              :options="[
                { value: '', label: 'Selecteer' },
                ...Array.from({ length: 30 }, (_, i) => ({
                  value: i + 1,
                  label: i === 0 ? 'Week' : `Om de ${i + 1} weken`,
                })),
              ]"
              placeholder="Selecteer"
            />
          </div>

          <div class="repeat-field">
            <label>Plan om</label>
            <div class="input-icon-wrapper">
              <Timer :stroke-width="1" class="input-icon" />
              <input type="time" v-model="repeatData.planOm" />
            </div>
          </div>

          <div class="repeat-field">
            <label>End date (incl.)</label>
            <div
              style="
                position: relative;
                display: flex;
                align-items: center;
                gap: 0.5em;
              "
            >
              <span
                style="
                  position: absolute;
                  left: 1em;
                  pointer-events: none;
                  z-index: 2;
                  display: flex;
                  align-items: center;
                  height: 100%;
                "
              >
                <Calendar
                  :stroke-width="1"
                  style="width: 1.2em; height: 1.2em; color: #fff; opacity: 0.7"
                />
              </span>
              <input
                type="text"
                class="date-input"
                :value="formatRepeatEndDate(repeatData.endDate)"
                readonly
                @click="showRepeatEndDateCalendar = true"
                placeholder="Kies een datum"
                style="
                  padding-left: 3em;
                  cursor: pointer;
                  background: #222;
                  color: #fff;
                  border-radius: 6px;
                  border: 1px solid #444;
                  padding: 0.5rem 1rem;
                  width: 100%;
                "
              />
              <button
                v-if="repeatData.endDate"
                type="button"
                @click="clearRepeatEndDate"
                style="
                  background: none;
                  border: none;
                  color: #fff;
                  cursor: pointer;
                  position: absolute;
                  right: 0.5rem;
                "
              >
                &times;
              </button>
              <div
                v-if="showRepeatEndDateCalendar"
                class="calendar-popup"
                @click.stop
              >
                <CalendarComponent
                  :model-value="
                    repeatData.endDate
                      ? new Date(repeatData.endDate)
                      : new Date()
                  "
                  @update:modelValue="selectRepeatEndDate"
                  :min-value="null"
                  :max-value="null"
                  layout="month-and-year"
                  class="custom-calendar"
                />
              </div>
            </div>
          </div>

          <div v-if="nextRepeatDates.length > 0" class="repeat-preview-box">
            <div class="repeat-preview-title">
              <strong>Eerste voorkomens:</strong>
            </div>
            <ul class="repeat-preview-list">
              <li
                v-for="(date, idx) in nextRepeatDates.slice(0, 4)"
                :key="idx"
                class="repeat-preview-item"
              >
                {{ formatDateShort(date) }}
              </li>
            </ul>
            <div class="repeat-preview-count">
              <strong>Aantal herhalingen: {{ nextRepeatDates.length }}</strong>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <AppButton type="submit" variant="primary"
            >Opslaan en inplannen</AppButton
          >
          <AppButton type="submit" variant="primary">Opslaan</AppButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/auth.js";
import { X, Search, Timer, Calendar } from "lucide-vue-next";
import ToggleSwitch from "./ToggleSwitch.vue";
import CustomDropdown from "./CustomDropdown.vue";
import AppButton from "./AppButton.vue";
import CalendarComponent from "@/components/ui/calendar/Calendar.vue";

export default {
  name: "AddTaskPopup",
  components: {
    X,
    Search,
    Timer,
    ToggleSwitch,
    CustomDropdown,
    AppButton,
    Calendar,
    CalendarComponent,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projects: Array,
    deals: Array,
    users: Array,
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchClient: "",
      clientResults: [],
      selectedClient: null,
      selectedProject: "",
      selectedDeal: "",
      taskType: "administration",
      taskTitle: "",
      taskDescription: "",
      responsible: "",
      dueDate: new Date(),
      showDueDateCalendar: false,
      priority: "",
      repeat: false,
      duration: "00:30",
      errors: [],
      showClientSuggestions: false,
      activeClientIndex: -1,
      loadedUsers: [], // Users fetched from API
      loadedProjects: [], // Projects fetched from API
      loadedDeals: [], // Deals fetched from API
      // deals: [], // Dynamisch opgehaalde deals
      showRepeatOptions: false,
      showRepeatEndDateCalendar: false,
      repeatData: {
        pattern: null,
        interval: 1,
        planOm: "12:30",
        endDate: "",
      },
    };
  },
  async created() {
    // Fetch users, projects, and deals from API
    await this.fetchUsers();
    await this.fetchProjects();
    await this.fetchDeals();
  },
  watch: {
    visible(newVal) {
      if (newVal && this.initialData) {
        this.applyInitialData();
      }
      // Disable/enable scrolling op de body wanneer popup open/dicht gaat
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  computed: {
    filteredClientResults() {
      if (!Array.isArray(this.clientResults)) return [];
      const term = (this.searchClient || "").trim().toLowerCase();
      if (!term) return this.clientResults;
      return this.clientResults.filter((c) =>
        (c.name || "").toLowerCase().includes(term)
      );
    },
    nextRepeatDates() {
      if (
        !this.repeatData.pattern ||
        !this.repeatData.endDate ||
        !this.dueDate
      ) {
        return [];
      }

      const startDate =
        this.dueDate instanceof Date ? this.dueDate : new Date(this.dueDate);
      const endDateStr = this.repeatData.endDate;
      let endDate;
      if (typeof endDateStr === "string") {
        const [year, month, day] = endDateStr.split("-").map(Number);
        endDate = new Date(year, month - 1, day, 23, 59, 59);
      } else {
        endDate = new Date(endDateStr);
      }

      const pattern = this.repeatData.pattern;
      const interval = this.repeatData.interval || 1;
      const dates = [];

      // Haal uren en minuten uit 'Plan om' veld (HH:MM format)
      let planOmHours = 0;
      let planOmMinutes = 0;
      if (this.repeatData.planOm) {
        const [hours, minutes] = this.repeatData.planOm.split(":").map(Number);
        planOmHours = hours || 0;
        planOmMinutes = minutes || 0;
      }

      // Voeg eerst de startDate zelf toe (als deze voldoet aan het patroon)
      let currentDate = new Date(startDate);
      currentDate.setHours(planOmHours, planOmMinutes, 0, 0);

      // Genereer maximaal 20 volgende tasks
      while (currentDate <= endDate && dates.length < 20) {
        const dayOfWeek = currentDate.getDay();
        const isWorkday = dayOfWeek >= 1 && dayOfWeek <= 5; // Ma-Vr

        let shouldAdd = false;

        switch (pattern) {
          case "daily":
            shouldAdd = true;
            break;
          case "workdays":
            shouldAdd = isWorkday;
            break;
          case "weekly":
            // Controleer of dit dezelfde dag van de week is als startDate
            // en of het een veelvoud van interval weken is
            if (currentDate.getDay() === startDate.getDay()) {
              const daysDiff = Math.floor(
                (currentDate - startDate) / (1000 * 60 * 60 * 24)
              );
              const weeksDiff = Math.floor(daysDiff / 7);
              shouldAdd = weeksDiff === 0 || weeksDiff % interval === 0;
            }
            break;
          case "monthly":
            shouldAdd = currentDate.getDate() === startDate.getDate();
            break;
          case "yearly":
            shouldAdd =
              currentDate.getMonth() === startDate.getMonth() &&
              currentDate.getDate() === startDate.getDate();
            break;
        }

        if (shouldAdd && currentDate <= endDate) {
          dates.push(new Date(currentDate));
        }

        // Ga naar volgende dag
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    },
  },
  methods: {
    formatDate(dateObj) {
      if (!dateObj || !(dateObj instanceof Date)) {
        return "";
      }
      const days = [
        "Zondag",
        "Maandag",
        "Dinsdag",
        "Woensdag",
        "Donderdag",
        "Vrijdag",
        "Zaterdag",
      ];
      const dayName = days[dateObj.getDay()];
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const year = dateObj.getFullYear();
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      return `${dayName} ${day}/${month}/${year} om ${hours}u${minutes}`;
    },
    formatDateShort(dateObj) {
      if (!dateObj || !(dateObj instanceof Date)) {
        return "";
      }
      const days = ["Zon", "Maa", "Din", "Woe", "Don", "Vri", "Zat"];
      const dayName = days[dateObj.getDay()];
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const year = String(dateObj.getFullYear()).slice(-2);
      return `${dayName} ${day}/${month}/${year}`;
    },
    async fetchUsers() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/users",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedUsers = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    async fetchProjects() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/projects",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedProjects = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    },
    async fetchDeals() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/deals",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedDeals = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching deals:", error);
      }
    },
    applyInitialData() {
      // Apply initial data from draw-to-create feature
      if (!this.initialData) return;

      // Set the date from initialData.date (format: "YYYY-MM-DD")
      if (this.initialData.date) {
        const [year, month, day] = this.initialData.date.split("-").map(Number);
        this.dueDate = new Date(year, month - 1, day);
      }

      // For tasks, we can also set duration if needed
      if (this.initialData.duration !== undefined) {
        const totalSeconds = this.initialData.duration;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        this.duration = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;
      }
    },
    async onClientInput() {
      this.showClientSuggestions = true;
      this.activeClientIndex = -1;
      await this.updateClientResults();
    },
    async onClientFocus() {
      this.showClientSuggestions = true;
      this.activeClientIndex = -1;
      if (!this.searchClient || this.searchClient.length < 2) {
        await this.fetchAllClients();
      } else {
        await this.updateClientResults();
      }
    },
    onClientBlur() {
      setTimeout(() => {
        this.showClientSuggestions = false;
      }, 120);
    },
    async updateClientResults() {
      // Real API search
      const query =
        this.searchClient && this.searchClient.length >= 2
          ? this.searchClient
          : "";
      let url =
        "https://netnest-internal-platform-backend-2dow.onrender.com/api/companies";
      if (query) {
        url += `?q=${encodeURIComponent(query)}`;
      }
      const token = AuthService.token;
      try {
        this.clientResults = [];
        this.clientLoading = true;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        // Accept both {data: [...]} and [...] responses
        const companies = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
          ? data.data
          : [];
        this.clientResults = companies.map((c) => ({
          id: c._id || c.id,
          name: c.name || c.companyName || c.title || "",
        }));
      } catch (e) {
        this.clientResults = [];
      } finally {
        this.clientLoading = false;
      }
    },
    async fetchAllClients() {
      // Real API fetch all
      let url =
        "https://netnest-internal-platform-backend-2dow.onrender.com/api/companies";
      const token = AuthService.token;
      try {
        this.clientResults = [];
        this.clientLoading = true;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        const companies = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
          ? data.data
          : [];
        this.clientResults = companies.map((c) => ({
          id: c._id || c.id,
          name: c.name || c.companyName || c.title || "",
        }));
      } catch (e) {
        this.clientResults = [];
      } finally {
        this.clientLoading = false;
      }
    },
    moveClientSelection(dir) {
      if (!this.showClientSuggestions || !this.clientResults.length) return;
      let idx = this.activeClientIndex + dir;
      if (idx < 0) idx = this.clientResults.length - 1;
      if (idx >= this.clientResults.length) idx = 0;
      this.activeClientIndex = idx;
    },
    selectHighlightedClient() {
      if (
        this.showClientSuggestions &&
        this.activeClientIndex >= 0 &&
        this.activeClientIndex < this.clientResults.length
      ) {
        this.selectClient(this.clientResults[this.activeClientIndex]);
      }
    },
    selectClient(client) {
      this.selectedClient = client;
      this.searchClient = client.name;
      this.clientResults = [];
      this.showClientSuggestions = false;
      this.activeClientIndex = -1;
    },
    formatDueDate(val) {
      if (!val) return "";
      let dateObj = val;
      if (typeof val === "string" && !isNaN(Date.parse(val))) {
        dateObj = new Date(val);
      } else if (val && typeof val.toDate === "function") {
        dateObj = val.toDate();
      }
      if (dateObj instanceof Date && !isNaN(dateObj)) {
        const d = dateObj.getDate().toString().padStart(2, "0");
        const m = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const y = dateObj.getFullYear();
        return `${d}/${m}/${y}`;
      }
      return "";
    },
    clearDueDate() {
      this.dueDate = "";
    },
    clearRepeatEndDate() {
      this.repeatData.endDate = "";
    },
    formatRepeatEndDate(val) {
      if (!val) return "";
      let dateObj = val;
      if (typeof val === "string" && !isNaN(Date.parse(val))) {
        dateObj = new Date(val);
      } else if (val && typeof val.toDate === "function") {
        dateObj = val.toDate();
      }
      if (dateObj instanceof Date && !isNaN(dateObj)) {
        const d = dateObj.getDate().toString().padStart(2, "0");
        const m = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const y = dateObj.getFullYear();
        return `${d}/${m}/${y}`;
      }
      return "";
    },
    selectRepeatEndDate(date) {
      if (date) {
        // Zet de datum in ISO formaat (YYYY-MM-DD)
        this.repeatData.endDate = date.toISOString().split("T")[0];
      }
      this.showRepeatEndDateCalendar = false;
    },
    logAndSubmitTask() {
      this.submitTask();
    },
    async submitTask(e) {
      // Bepaal of het inplannen is of niet
      let planned = false;
      if (
        e &&
        e.target &&
        e.target.innerText &&
        e.target.innerText.toLowerCase().includes("inplannen")
      ) {
        planned = true;
      }
      // Zorg dat dueDate een geldige ISO string is
      let dueDateISO = this.dueDate;
      if (dueDateISO instanceof Date) {
        dueDateISO = dueDateISO.toISOString();
      } else if (
        typeof dueDateISO === "string" &&
        !isNaN(Date.parse(dueDateISO))
      ) {
        dueDateISO = new Date(dueDateISO).toISOString();
      }

      // Get current user ID for createdBy
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentUserId = user._id || user.id;

      // Verzenden naar backend
      // Convert duration from "HH:MM" to seconds
      const [durationHours, durationMinutes] = this.duration.split(":");
      const durationSeconds =
        parseInt(durationHours) * 3600 + parseInt(durationMinutes) * 60;

      // Build repeatData if repeat is enabled
      let repeatDataToSend = null;
      if (
        this.showRepeatOptions &&
        this.repeatData.pattern &&
        this.repeatData.endDate
      ) {
        repeatDataToSend = {
          pattern: this.repeatData.pattern,
          interval: this.repeatData.interval || 1,
          planOm: this.repeatData.planOm || "12:30",
          endDate: this.repeatData.endDate, // Already in YYYY-MM-DD format
        };
      }

      const taskObj = {
        client: this.selectedClient,
        companyId: this.selectedClient ? this.selectedClient.id : undefined,
        project: this.selectedProject,
        deal: this.selectedDeal,
        type: "task",
        title: this.taskTitle,
        description: this.taskDescription,
        responsible: this.responsible || currentUserId, // Default to current user if not set
        dueDate: dueDateISO,
        deliveryDate: dueDateISO,
        date: dueDateISO, // <-- stuur 'date' mee zoals bij appointment/call
        priority: this.priority,
        repeat: this.showRepeatOptions && !!repeatDataToSend,
        repeatData: repeatDataToSend,
        duration: durationSeconds, // Send duration in seconds
        workType: this.taskType, // workType from taskType dropdown
        projectNumber: this.selectedProject, // or from selected project
        planned,
        createdBy: currentUserId,
      };
      try {
        const token = AuthService.token;
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/activities",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(taskObj),
          }
        );
        const data = await res.json();
        let activity = data && data.data && data.data._id ? data.data : taskObj;

        // Agenda-entry direct aanmaken
        // Bepaal projectId (kan string of object zijn)
        let projectId =
          activity.project?._id ||
          activity.project ||
          activity.projectId ||
          activity.projectNumber;
        // userId: verantwoordelijke of creator
        let userId =
          activity.responsible || activity.createdBy || currentUserId;
        // date: altijd YYYY-MM-DD
        let dateObj = new Date(
          activity.date || activity.deliveryDate || dueDateISO
        );
        let dateStr = `${dateObj.getUTCFullYear()}-${String(
          dateObj.getUTCMonth() + 1
        ).padStart(2, "0")}-${String(dateObj.getUTCDate()).padStart(2, "0")}`;
        // startAt: seconden sinds 00:00
        let startAt =
          dateObj.getUTCHours() * 3600 + dateObj.getUTCMinutes() * 60;
        // task: titel
        let taskTitle = activity.title || activity.taskTitle || "Taak";
        // duration: in seconden
        let duration = activity.duration;
        // agenda payload
        const agendaPayload = {
          userId,
          projectId,
          date: dateStr,
          task: taskTitle,
          duration,
          startAt,
        };
        try {
          await fetch("/api/Agenda", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(agendaPayload),
          });
        } catch (err) {
          console.warn("Kon agenda-item niet aanmaken:", err);
        }

        this.$emit("task-created", activity);
        this.$emit("close");
      } catch (e) {
        console.error("Error creating task:", e);
        this.$emit("task-created", taskObj);
        this.$emit("close");
      }
    },
    // fetchDeals removed, deals come from props
  },
};
</script>
<style scoped>
/* Calendar popup styling zoals in DealAdd.vue */
.calendar-popup {
  position: absolute;
  top: 2.5em;
  left: 0;
  z-index: 100;
  background: #18181b;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}
.date-input:focus {
  box-shadow: 0 0 0 2px var(--primary_color, #4f46e5);
}
.date-input {
  padding-left: 3em !important;
}
.date-input::placeholder {
  color: #bbb;
  opacity: 1;
}
.form-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.form-block-title {
  font-weight: 600;
  color: #fff;
}
.add-task-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem 0;
  overflow-y: auto;
}
.add-task-popup {
  background: var(--surface);
  border-radius: 0.75rem;
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.12);
  padding: 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 90vh;
  overflow-y: auto;
}
.add-task-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.add-task-popup form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.close-icon {
  width: 1.2rem;
  height: 1.2rem;
  cursor: pointer;
  color: #fff;
  transition: color 0.2s;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
}
.form-section {
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
}
.form-section label {
  margin-bottom: 0.5rem;
}
.form-section input,
.form-section select,
.form-section textarea {
  padding: 0.5rem;
  border-radius: 4px;
}
.form-section textarea {
  min-height: 60px;
}
.single-col {
  margin-top: 0.5rem;
  width: 100%;
}
.single-col input,
.single-col textarea {
  width: 100%;
  max-width: 100%;
}
.search-results {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-top: 0.25rem;
  list-style: none;
  padding: 0;
  max-height: 120px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}
.search-results li {
  padding: 0.5rem 1rem;
  cursor: pointer;
}
.search-results li:hover {
  background: #f3f4f6;
}
.form-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  grid-column: 1 / span 2;
}
.input-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--input-bg, #222);
  border-radius: 0.5rem;
}
.input-icon {
  position: absolute;
  width: 1rem;
  height: 1rem;
  color: #fff;
  pointer-events: none;
  left: 0.5rem;
}
.input-icon-wrapper.input-icon-right .input-icon {
  right: 0.75rem;
  left: auto;
}
.input-icon-wrapper.input-icon-right input {
  padding-left: 0.5rem;
  padding-right: 2rem;
}
.input-icon-wrapper:not(.input-icon-right) input {
  padding-left: 2rem;
}

/* Specifiek voor timer input: padding links voor icon */
.input-icon-wrapper input[type="time"] {
  padding-left: 2rem;
}
.grid-span-2 {
  grid-column: 1 / span 2;
  width: 100%;
  max-width: 100%;
}
/* --- Prospectie-style suggestions dropdown for client search (from DealAdd.vue) --- */
.prospectie-suggestions-dropdown {
  background: #222;
  border: 1px solid #444;
  border-radius: 6px;
  list-style: none;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  z-index: 40;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
}
.prospectie-suggestions-dropdown .suggestion-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.prospectie-suggestions-dropdown .suggestion-item.active,
.prospectie-suggestions-dropdown .suggestion-item:hover {
  background: var(--primary_color, #4f46e5);
  color: #fff;
}
.prospectie-suggestions-dropdown .suggestion-text {
  font-weight: 500;
  color: #fff;
}
/* Zorg dat de client dropdown onder het inputveld staat */
.client-search-section {
  position: relative;
  z-index: 30;
}

.repeat-section {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.repeat-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.repeat-field label {
  font-weight: 500;
  color: #fff;
}

.repeat-field input,
.repeat-field select {
  width: 100%;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #444;
  background: #222;
  color: #fff;
}

.repeat-field :deep(.custom-dropdown) {
  width: 100%;
}

.repeat-section .form-section {
  margin-bottom: 0.5rem;
}

.repeat-preview-box {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--surface-light, #1e1e1e);
  border-radius: 0.5rem;
}

.repeat-preview {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #444;
}

.repeat-preview-title {
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.repeat-preview-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.repeat-preview-item {
  padding: 0.5rem;
  background-color: #222;
  border-left: 3px solid #00ff00;
  padding-left: 0.75rem;
  margin-bottom: 0.5rem;
  color: #fff;
  border-radius: 2px;
}

.repeat-preview-count {
  margin-top: 0.75rem;
}
</style>
